import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Plus, Search, ChevronUp, ChevronDown, Pencil, Trash2, ExternalLink, Loader2 } from 'lucide-react';
import { fetchApps, deleteApp, moveApp } from '../../utils/adminApi';
import { resolveImage } from '../../utils/imageHelper';

const AppsList = () => {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [params, setParams] = useSearchParams();
  const listFilter = params.get('list') || 'all';

  const load = async () => {
    setLoading(true);
    try {
      const data = await fetchApps();
      setApps(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete "${name}"? This cannot be undone.`)) return;
    await deleteApp(id);
    load();
  };

  const handleMove = async (id, dir) => {
    await moveApp(id, dir);
    load();
  };

  const filtered = apps
    .filter((a) => listFilter === 'all' || a.listType === listFilter)
    .filter((a) => a.name.toLowerCase().includes(q.toLowerCase()) || a.slug.toLowerCase().includes(q.toLowerCase()));

  const setList = (v) => {
    if (v === 'all') setParams({});
    else setParams({ list: v });
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-semibold text-gray-900">All Apps</h1>
        <Link to="/wp-admin/apps/new" className="inline-flex items-center gap-1.5 bg-[#2271b1] hover:bg-[#135e96] text-white text-sm font-medium px-3 py-1.5 rounded transition-colors">
          <Plus className="w-4 h-4" /> Add New
        </Link>
      </div>

      <div className="bg-white border border-gray-200 rounded shadow-sm">
        <div className="px-3 py-2.5 border-b border-gray-200 flex flex-col sm:flex-row gap-2 sm:items-center sm:justify-between">
          <div className="flex items-center gap-2 text-sm">
            <span className="text-gray-600">Filter:</span>
            {[
              { k: 'all', label: `All (${apps.length})` },
              { k: 'top', label: `Top (${apps.filter(a => a.listType==='top').length})` },
              { k: 'main', label: `Main (${apps.filter(a => a.listType==='main').length})` },
              { k: 'latest', label: `Latest (${apps.filter(a => a.listType==='latest').length})` },
            ].map((t) => (
              <button
                key={t.k}
                onClick={() => setList(t.k)}
                className={`px-2 py-1 rounded ${listFilter === t.k ? 'bg-[#2271b1] text-white' : 'text-[#2271b1] hover:bg-blue-50'}`}
              >
                {t.label}
              </button>
            ))}
          </div>
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search apps..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-8 pr-3 py-1.5 text-sm border border-gray-300 rounded w-full sm:w-64 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {loading ? (
          <div className="p-8 text-center text-gray-500"><Loader2 className="w-6 h-6 animate-spin inline" /></div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-gray-500">No apps found.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600 border-b border-gray-200">
                <tr>
                  <th className="text-left px-3 py-2 font-medium">App</th>
                  <th className="text-left px-3 py-2 font-medium hidden md:table-cell">Slug</th>
                  <th className="text-left px-3 py-2 font-medium">List</th>
                  <th className="text-left px-3 py-2 font-medium hidden sm:table-cell">Bonus</th>
                  <th className="text-left px-3 py-2 font-medium hidden sm:table-cell">Order</th>
                  <th className="text-right px-3 py-2 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((a) => (
                  <tr key={a.id} className="border-b border-gray-100 hover:bg-blue-50/40">
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-3">
                        <img src={resolveImage(a.image)} alt={a.name} className="w-10 h-10 rounded object-cover border border-gray-200" />
                        <div>
                          <div className="font-medium text-gray-900">{a.name}</div>
                          <div className="md:hidden text-xs text-gray-500">{a.slug}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-2 hidden md:table-cell text-gray-600">{a.slug}</td>
                    <td className="px-3 py-2">
                      <span className={`inline-block text-xs px-2 py-0.5 rounded ${
                        a.listType === 'top' ? 'bg-yellow-100 text-yellow-800' :
                        a.listType === 'latest' ? 'bg-purple-100 text-purple-800' :
                        'bg-green-100 text-green-800'
                      }`}>{a.listType}</span>
                    </td>
                    <td className="px-3 py-2 hidden sm:table-cell text-gray-700">₹{a.bonus}</td>
                    <td className="px-3 py-2 hidden sm:table-cell text-gray-500">{a.order}</td>
                    <td className="px-3 py-2">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => handleMove(a.id, 'up')} title="Move up" className="p-1.5 hover:bg-gray-100 rounded"><ChevronUp className="w-4 h-4" /></button>
                        <button onClick={() => handleMove(a.id, 'down')} title="Move down" className="p-1.5 hover:bg-gray-100 rounded"><ChevronDown className="w-4 h-4" /></button>
                        <a href={`/app/${a.slug}`} target="_blank" rel="noopener noreferrer" title="View" className="p-1.5 hover:bg-gray-100 rounded"><ExternalLink className="w-4 h-4" /></a>
                        <Link to={`/wp-admin/apps/${a.id}/edit`} title="Edit" className="p-1.5 hover:bg-gray-100 rounded"><Pencil className="w-4 h-4 text-blue-600" /></Link>
                        <button onClick={() => handleDelete(a.id, a.name)} title="Delete" className="p-1.5 hover:bg-red-50 rounded"><Trash2 className="w-4 h-4 text-red-600" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AppsList;
